import { useContext } from "react";

import { InlineOption, InlineOptionsListControl } from "@/Common/Components/Inputs/InlineOptionsControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Checkbox } from "@/DesignSystem";
import { EncounterDateContext } from "@/Flowsheets/Contexts/EncounterDateContext";
import { BooleanReviewedCareElement } from "@/Flowsheets/Models/CareElements/BooleanReviewedCareElement";
import { SubjectCareElement } from "@/Flowsheets/Models/CareElements/SubjectCareElement";
import Notes from "../Notes";
import { CareElementDateReviewed } from "./CareElementDateReviewed";
import { CareElementWithFrequencyLabel } from "./CareElementWithFrequencyLabel";

import styles from "./CareElementSubjectDateReviewed.module.scss";

/**
 * Props for the CareElementSubjectDateReviewedControl component.
 * Represents the editable state, label, care element, and change handler.
 */
interface ICareElementBooleanNumberDateReviewedProps {
    /**
     * Indicates if the care element is editable.
     */
    isEditable: boolean;
    /**
     * The label for the care element.
     */
    label: string | JSX.Element;
    /**
     * The care element to be displayed.
     */
    element: SubjectCareElement;
    /**
     * Callback function to handle changes to the care element.
     * @param element - The updated SubjectCareElement.
     */
    onChange: (element: SubjectCareElement) => void;
}

/**
 * Subject date reviewed control for the subject care element.
 * @param props - The properties for the component.
 * @returns JSX.Element
 */
export function CareElementSubjectDateReviewedControl(props: Readonly<ICareElementBooleanNumberDateReviewedProps>): JSX.Element {
    const { element, onChange, isEditable, label } = props;
    const { getText } = useTranslation();
    const { encounterDateTimeWithPrecision, encounterDate } = useContext(EncounterDateContext);

    return (
        <>
            <div className={`${styles.careElementValueContainer} ${styles["side"]}`}>
                <div className={styles.prefixContainer}>
                    <Checkbox
                        id={`checkbox-${element.Name}`}
                        checked={element.IsScreened}
                        disabled={!isEditable}
                        onChange={value => {
                            const isValid = value === true && element.IsNormal !== null;

                            const shouldAutoFillDate = isValid && !element.DateReviewedWithPrecision?.value;

                            let dateToUse = null;

                            if (isValid) {
                                dateToUse = shouldAutoFillDate
                                    ? encounterDateTimeWithPrecision
                                    : element.DateReviewedWithPrecision;
                            }

                            onChange(new SubjectCareElement({ ...element, IsScreened: value, IsNormal: !value ? null : element.IsNormal, DateReviewedWithPrecision: dateToUse }));
                        }}
                    />
                </div>
                <div className={styles.nameContainer}>
                    <CareElementWithFrequencyLabel
                        label={label}
                        keyName={`${element.Name}-frequency`}
                        state={element}
                        encounterDate={encounterDate}
                    />
                </div>
                <div>
                    {element.IsScreened && (
                        <InlineOptionsListControl
                            id={`care-element-yes-no-${element?.Name}`}
                            value={element.IsNormal}
                            disabled={!isEditable}
                            items={[
                                new InlineOption<boolean>(true, getText("NormalLabel")),
                                new InlineOption<boolean>(false, getText("AbnormalLabel"))
                            ]}
                            onChange={option => {
                                const yesOrNoValue: boolean | null = (element?.IsNormal === option.Key) ? null : option.Key;

                                const isValid = yesOrNoValue !== null;

                                const shouldAutoFillDate = isValid && !element.DateReviewedWithPrecision?.value;

                                let dateToUse = null;

                                if (isValid) {
                                    dateToUse = shouldAutoFillDate
                                        ? encounterDateTimeWithPrecision
                                        : element.DateReviewedWithPrecision;
                                }

                                onChange(new SubjectCareElement({ ...element, IsNormal: yesOrNoValue, DateReviewedWithPrecision: dateToUse }));
                            }}
                        />
                    )}
                </div>
            </div>
            <CareElementDateReviewed
                id={`care-element-date-reviewed-${element?.Name}`}
                isEditable={isEditable}
                dataCy={`care-element-date-reviewed-${element?.Name}`}
                encounterDate={encounterDate}
                state={new BooleanReviewedCareElement({ ...element, Value: element.IsNormal, SubSectionName: "" })}
                handleChange={date => onChange(new SubjectCareElement({ ...element, DateReviewedWithPrecision: date }))}
                setFalseAsValid={true}
            />
            <Notes
                id={`care-element-notes-${element?.Name}`}
                dataCy={`care-element-notes-${element?.Name}`}
                isEditable={isEditable}
                label={getText("NotesLabel")}
                value={element.Notes ?? ""}
                onTextChange={value => onChange(new SubjectCareElement({ ...element, Notes: value }))}
            />
        </>
    );
}
